import { AdManagerPublishValidationError, TranslateFn } from './types';

const humanizeFieldName = (field: string) =>
  field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getTranslatedFieldLabel = (field: string, t: TranslateFn) => {
  const fieldLabels: Record<string, string> = {
    age_max: t('adManager.fields.ageMax', 'Maximum age'),
    age_min: t('adManager.fields.ageMin', 'Minimum age'),
    bid_strategy: t('adManager.fields.bidStrategy', 'Bid strategy'),
    budget_amount_cents: t('adManager.fields.budgetAmount', 'Budget'),
    dsa_beneficiary: t('adManager.fields.dsaBeneficiary', 'Beneficiary'),
    dsa_payor: t('adManager.fields.dsaPayor', 'Person who pays'),
    end_date: t('adManager.fields.endDate', 'End date'),
    gender: t('adManager.fields.gender', 'Gender'),
    headline: t('adManager.fields.headline', 'Headline'),
    landing_url: t('adManager.fields.landingUrl', 'Landing URL'),
    max_bid_amount_cents: t('adManager.fields.maxBid', 'Max bid'),
    media_image_uuids: t('adManager.fields.media', 'Media'),
    name: t('adManager.fields.campaignName', 'Campaign name'),
    pixel: t('adManager.fields.pixel', 'Meta pixel'),
    placements: t('adManager.fields.placements', 'Placements'),
    primary_text: t('adManager.fields.primaryText', 'Primary text'),
    radius_km: t('adManager.fields.radius', 'Radius'),
    special_ad_category_country: t(
      'adManager.fields.specialAdCategoryCountry',
      'Special ad country'
    ),
    start_date: t('adManager.fields.startDate', 'Start date'),
    target_behaviors: t('adManager.fields.behaviors', 'Behaviors'),
    target_event: t('adManager.fields.targetEvent', 'Target event'),
    target_interests: t('adManager.fields.interests', 'Interests'),
    target_locations: t('adManager.fields.targetLocations', 'Locations')
  };

  return (
    fieldLabels[field] ||
    t(`adManager.fields.${field}`, humanizeFieldName(field))
  );
};

const validationMessageFallbacks: Record<string, string> = {
  'adManager.validation.ageMaxOutOfRange':
    'Use a maximum age between 13 and 65.',
  'adManager.validation.ageMinOutOfRange':
    'Use a minimum age between 13 and 65.',
  'adManager.validation.ageRangeInvalid':
    'Use a maximum age greater than or equal to the minimum age.',
  'adManager.validation.campaignNameRequired': 'Enter a campaign name.',
  'adManager.validation.dailyBudgetMinimumDuration':
    'Use an end date at least 24 hours after the campaign starts.',
  'adManager.validation.dateInvalid': 'Choose a valid date.',
  'adManager.validation.dsaBeneficiaryRequired':
    'Enter the beneficiary for ads targeting this location.',
  'adManager.validation.dsaPayorRequired':
    'Enter the person who pays for ads targeting this location.',
  'adManager.validation.duplicateMedia': 'Remove duplicate media files.',
  'adManager.validation.endDateAfterComputedStart':
    'Choose an end date after the campaign start time.',
  'adManager.validation.endDateBeforeStartDate':
    'Choose an end date after the start date.',
  'adManager.validation.endDatePast': 'Choose today or a future date.',
  'adManager.validation.endDateRequired': 'Choose an end date.',
  'adManager.validation.facebookPageRequired':
    'Connect a Facebook page before publishing.',
  'adManager.validation.fixField': 'Please check this field.',
  'adManager.validation.headlineRequired': 'Enter a headline.',
  'adManager.validation.instagramAccountRequired':
    'Connect an Instagram account before using Instagram placements.',
  'adManager.validation.landingUrlRequired': 'Enter a landing URL.',
  'adManager.validation.manualBidUnsupported': 'Use automatic bidding.',
  'adManager.validation.maxBidInvalid': 'Enter a max bid greater than 0.',
  'adManager.validation.maxBidRequired': 'Enter a max bid.',
  'adManager.validation.mediaNotFound':
    'One or more selected media files could not be found.',
  'adManager.validation.mediaRequired': 'Add at least one media file.',
  'adManager.validation.mediaWrongLocation':
    'Use media from this location only.',
  'adManager.validation.mixedMedia':
    'Use either images or videos in one campaign, not both.',
  'adManager.validation.pixelRequired': 'Choose a Meta pixel.',
  'adManager.validation.pixelUnavailable':
    'Choose a Meta pixel available for this ad account.',
  'adManager.validation.pixelVerifyFailed':
    'Refresh the pixel list and choose the Meta pixel again.',
  'adManager.validation.placementsRequired': 'Choose at least one placement.',
  'adManager.validation.primaryTextRequired': 'Enter primary text.',
  'adManager.validation.specialAdAgeMax':
    'Use 65 as the maximum age for this special ad category.',
  'adManager.validation.specialAdAgeMin':
    'Use 18 as the minimum age for this special ad category.',
  'adManager.validation.specialAdCountryMismatch':
    'Use target locations that match the special ad country.',
  'adManager.validation.specialAdCountryRequired':
    'Choose target locations with a supported country.',
  'adManager.validation.specialAdDetailedTargetingUnsupported':
    'Remove interests and behaviors for this special ad category.',
  'adManager.validation.specialAdGenderAll':
    'Use all genders for this special ad category.',
  'adManager.validation.specialAdLocationRadiusRequired':
    'Use a radius of at least 25km for each target location.',
  'adManager.validation.specialAdRadiusRequired':
    'Use a radius of at least 25km for this special ad category.',
  'adManager.validation.specialAdTargetingInvalid':
    'Check locations, age, gender, interests, and behaviors for this special ad category.',
  'adManager.validation.startDatePast': 'Choose today or a future date.',
  'adManager.validation.startDateRequired': 'Choose a start date.',
  'adManager.validation.targetEventRequired': 'Choose a target event.',
  'adManager.validation.targetEventUnavailableForObjective':
    'Choose a conversion event that matches the selected objective.',
  'adManager.validation.targetEventUnsupported':
    'Choose a supported target event.',
  'adManager.validation.targetLocationRequired':
    'Add at least one target city or country.',
  'adManager.validation.unsupportedMedia':
    'Use image or video media files only.',
  'adManager.validation.unsupportedMediaFormat':
    'Use JPEG, PNG, MP4, or MOV files only.'
};

const validationMessageKeyByText: Record<string, string> = {
  'A connected Facebook page is required before publishing.':
    'adManager.validation.facebookPageRequired',
  'A connected Instagram account is required for Instagram placements.':
    'adManager.validation.instagramAccountRequired',
  'All media images must belong to the campaign location.':
    'adManager.validation.mediaWrongLocation',
  'At least one media file is required before publishing.':
    'adManager.validation.mediaRequired',
  'At least one placement is required before publishing.':
    'adManager.validation.placementsRequired',
  'At least one publishable target city or country is required.':
    'adManager.validation.targetLocationRequired',
  'At least one target city or country is required before publishing.':
    'adManager.validation.targetLocationRequired',
  'Campaign name is required before publishing.':
    'adManager.validation.campaignNameRequired',
  'Beneficiary is required for ads targeting this location.':
    'adManager.validation.dsaBeneficiaryRequired',
  'Daily budget campaigns must run for at least 24 hours.':
    'adManager.validation.dailyBudgetMinimumDuration',
  'Duplicate image UUIDs are not allowed.':
    'adManager.validation.duplicateMedia',
  'End date cannot be in the past.': 'adManager.validation.endDatePast',
  'End date is required before publishing.':
    'adManager.validation.endDateRequired',
  'End date must be after the calculated Meta start time.':
    'adManager.validation.endDateAfterComputedStart',
  'Detailed targeting is not available for this special ad category.':
    'adManager.validation.specialAdDetailedTargetingUnsupported',
  'Special ad category targeting is invalid. Check locations, detailed targeting, age, and gender.':
    'adManager.validation.specialAdTargetingInvalid',
  'gender must be all for this special ad category.':
    'adManager.validation.specialAdGenderAll',
  'Headline is required before publishing.':
    'adManager.validation.headlineRequired',
  'Landing URL is required before publishing.':
    'adManager.validation.landingUrlRequired',
  'Manual bid strategy is not supported yet.':
    'adManager.validation.manualBidUnsupported',
  'max_bid_amount_cents is required for manual bid campaigns.':
    'adManager.validation.maxBidRequired',
  'Meta pixel is required for this objective.':
    'adManager.validation.pixelRequired',
  'One or more images could not be found.':
    'adManager.validation.mediaNotFound',
  'Only image and video media files can be published as Meta ads.':
    'adManager.validation.unsupportedMedia',
  'Only JPEG, PNG, MP4, and MOV media files are supported.':
    'adManager.validation.unsupportedMediaFormat',
  'Primary text is required before publishing.':
    'adManager.validation.primaryTextRequired',
  'Person who pays is required for ads targeting this location.':
    'adManager.validation.dsaPayorRequired',
  'radius_km must be at least 25 for special ad category campaigns.':
    'adManager.validation.specialAdRadiusRequired',
  'Selected Meta pixel could not be verified. Please refresh the pixel list and try again.':
    'adManager.validation.pixelVerifyFailed',
  'Selected Meta pixel is not available for this ad account.':
    'adManager.validation.pixelUnavailable',
  'Selected target event is not supported.':
    'adManager.validation.targetEventUnsupported',
  'Selected target event is not available for this objective.':
    'adManager.validation.targetEventUnavailableForObjective',
  'Start date cannot be in the past.': 'adManager.validation.startDatePast',
  'Start date is required before publishing.':
    'adManager.validation.startDateRequired',
  'special_ad_category_country could not be derived from target locations.':
    'adManager.validation.specialAdCountryRequired',
  'Target event is required for this objective.':
    'adManager.validation.targetEventRequired',
  'target_locations radius_km must be at least 25 for special ad category campaigns.':
    'adManager.validation.specialAdLocationRadiusRequired',
  'Use target locations that match the special ad country.':
    'adManager.validation.specialAdCountryMismatch',
  'Use either images or videos in one campaign, not both.':
    'adManager.validation.mixedMedia',
  'age_max must be 65 for this special ad category.':
    'adManager.validation.specialAdAgeMax',
  'age_max must be greater than or equal to age_min.':
    'adManager.validation.ageRangeInvalid',
  'age_min must be 18 for this special ad category.':
    'adManager.validation.specialAdAgeMin',
  'end_date cannot be in the past.': 'adManager.validation.endDatePast',
  'end_date is required.': 'adManager.validation.endDateRequired',
  'end_date is required before publishing.':
    'adManager.validation.endDateRequired',
  'end_date is required for campaign ads.':
    'adManager.validation.endDateRequired',
  'end_date must be greater than or equal to start_date.':
    'adManager.validation.endDateBeforeStartDate',
  'start_date cannot be in the past.': 'adManager.validation.startDatePast'
};

const getTranslatedValidationKey = (messageKey: string, t: TranslateFn) =>
  t(
    messageKey,
    validationMessageFallbacks[messageKey] ||
      validationMessageFallbacks['adManager.validation.fixField']
  );

const getTranslatedValidationMessage = (
  field: string,
  message: string,
  t: TranslateFn
) => {
  const normalizedMessage = message.trim();

  if (validationMessageFallbacks[normalizedMessage]) {
    return getTranslatedValidationKey(normalizedMessage, t);
  }

  const messageKey = validationMessageKeyByText[normalizedMessage];
  if (messageKey) return getTranslatedValidationKey(messageKey, t);

  if (field === 'start_date' || field === 'end_date') {
    return getTranslatedValidationKey('adManager.validation.dateInvalid', t);
  }

  return getTranslatedValidationKey('adManager.validation.fixField', t);
};

const flattenFieldErrorMessages = (messages: any): string[] => {
  if (Array.isArray(messages))
    return messages.flatMap(flattenFieldErrorMessages);
  if (typeof messages === 'string') return [messages];
  if (!messages || typeof messages !== 'object') return [];

  return Object.values(messages).flatMap(flattenFieldErrorMessages);
};

export const getFieldErrorSummary = (
  fieldErrors: Record<string, any>,
  t: TranslateFn
) =>
  Object.entries(fieldErrors)
    .filter(([field]) => field !== 'detail')
    .flatMap(([field, messages]) =>
      flattenFieldErrorMessages(messages).map((message) => {
        const translatedMessage = getTranslatedValidationMessage(
          field,
          message,
          t
        );

        if (field === 'non_field_errors') return translatedMessage;

        return `${getTranslatedFieldLabel(field, t)}: ${translatedMessage}`;
      })
    );

export const getPublishValidationSummary = (
  validationErrors: AdManagerPublishValidationError[],
  t: TranslateFn
) =>
  validationErrors.map((error) =>
    getTranslatedValidationMessage(error.field, error.message, t)
  );

const isUpstreamGatewayError = (error: any): boolean => {
  const status = error?.response?.status;
  // 5xx from Cloudflare/upstream proxies (502 bad gateway, 503 service
  // unavailable, 504 gateway timeout, 520-527 cloudflare-specific), plus
  // "no response" network failures.
  if (typeof status === 'number' && status >= 502 && status <= 599) return true;
  if (error?.request && !error?.response) return true;

  const data = error?.response?.data;
  if (typeof data === 'string') {
    // Cloudflare error pages come back as HTML with recognisable text.
    if (/<html|<!doctype/i.test(data)) return true;
    if (/cloudflare|origin web server|bad gateway/i.test(data)) return true;
  }
  return false;
};

export const getRequestErrorMessage = (
  error: any,
  fallback: string,
  t?: TranslateFn
) => {
  // Prefer a real backend message when it exists — validation issues from
  // DRF come back as {detail: "..."} even when the HTTP status is in the
  // 5xx range, and those messages are useful.
  if (typeof error?.response?.data?.detail === 'string') {
    return error.response.data.detail;
  }
  // No structured backend message: if the failure looks like a Cloudflare /
  // upstream problem, tell the user to check their Meta connection instead
  // of surfacing raw HTML/proxy text.
  if (t && isUpstreamGatewayError(error)) {
    return t(
      'adManager.metaConnectionRefresh',
      "Couldn't reach Meta. Verify your Meta ad account connection and refresh."
    );
  }
  return fallback;
};

export const getTranslatedAdManagerErrorMessage = (
  message: string,
  t: TranslateFn
) => {
  const normalizedMessage = message.trim();
  const messageByText: Record<string, string> = {
    'Detailed targeting is not available for this special ad category.': t(
      'adManager.validation.specialAdDetailedTargetingUnsupported',
      'Remove interests and behaviors for this special ad category.'
    ),
    'Special ad category targeting is invalid. Check locations, detailed targeting, age, and gender.':
      t(
        'adManager.validation.specialAdTargetingInvalid',
        'Check locations, age, gender, interests, and behaviors for this special ad category.'
      ),
    'Selected target event is not available for this objective.': t(
      'adManager.validation.targetEventUnavailableForObjective',
      'Choose a conversion event that matches the selected objective.'
    ),
    'Ads targeting this location require DSA payor and beneficiary information.':
      t(
        'adManager.validation.dsaPayorBeneficiaryRequired',
        'Ads targeting this location require advertiser information.'
      ),
    'Meta app must be public before this ad can be published.': t(
      'adManager.validation.metaAppMustBePublic',
      'Meta app must be public before this ad can be published.'
    )
  };

  return messageByText[normalizedMessage] || message;
};
